/* eslint-disable react-refresh/only-export-components */
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { ChevronDown, LucideIcon } from 'lucide-react'
import * as React from 'react'
import { Spinner } from './spinner'

import { cn } from '@eigtech/ui/lib/utils'
import { ButtonGroupContext } from './button/button-group-context'
import { ButtonProps, ButtonVariantProps, buttonVariants } from './button/button-variants'

function Button({
  render,
  children,
  className,
  selected,
  variant = 'primary',
  radius = 'md',
  appearance = 'default',
  mode = 'default',
  size = 'md',
  autoHeight,
  underlined,
  underline,
  disabled: disabledProp,
  placeholder = false,
  isLoading = false,
  loadingText,
  ...props
}: ButtonProps) {
  const buttonGroupContext = React.useContext(ButtonGroupContext)

  const disabled = disabledProp || buttonGroupContext.disabled || isLoading

  const inner =
    isLoading && loadingText ? (
      <>
        <Spinner className="size-[1.25em]" />
        <span className="contents">{loadingText}</span>
      </>
    ) : (
      <>
        {isLoading && (
          <Spinner className="absolute left-1/2 top-1/2 size-[1.25em] -translate-x-1/2 -translate-y-1/2" />
        )}

        <span className={cn('contents', isLoading && 'invisible')}>{children}</span>
      </>
    )

  const finalProps = mergeProps(
    {
      className: cn(
        buttonVariants({
          variant: variant || buttonGroupContext.variant,
          size: size || buttonGroupContext.size,
          radius: radius || buttonGroupContext.radius,
          appearance: appearance || buttonGroupContext.appearance,
          mode: mode || buttonGroupContext.mode,
          autoHeight: autoHeight || buttonGroupContext.autoHeight,
          placeholder: placeholder || buttonGroupContext.placeholder,
          underlined: underlined || buttonGroupContext.underlined,
          underline: underline || buttonGroupContext.underline,
          className,
        }),
        render && disabled && 'pointer-events-none opacity-50'
      ),
      disabled,
      type: 'button',
    },
    { ...props, children: inner }
  )

  const element = useRender({
    render: render || <button />,
    props: finalProps,
    state: {
      slot: 'button',
      variant,
      mode,
      size,
      radius,
      appearance,
      ...(selected && { state: 'open' }),
    },
  })

  return element
}

interface ButtonArrowProps extends React.SVGProps<SVGSVGElement> {
  icon?: LucideIcon
}

function ButtonArrow({ icon: Icon = ChevronDown, className, ...props }: ButtonArrowProps) {
  return <Icon className={cn('-me-1 ms-auto', className)} data-slot="button-arrow" {...props} />
}

export { Button, ButtonArrow, buttonVariants }
export type { ButtonProps, ButtonVariantProps }
